import { RotateCcw } from "lucide-react";
import {
  formatLocalClock,
  queuePickedUpBanner,
  type GroomingQueuePickedUp,
  type LocalClockOptions,
} from "@ttc-spectree/toolbox-contract";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * The info line after the queue started again by itself (epic
 * PI-SPEC-SW-E290, feature PI-SPEC-SW-1479; frame B3 "sleep" row;
 * 01-roles.md section 9.6). No button: there is nothing to do.
 * - after a wake: "Picked up again after this computer woke at 7:58 AM. 5 briefs still to do.";
 * - after a fix: "Picked up again after the fix at 9:12 AM. 5 briefs still to do.";
 * - after the connection came back: "Picked up again when the connection came back at 9:12 AM."
 */

function briefsLeft(left: number): string {
  return `${String(left)} ${left === 1 ? "brief" : "briefs"} still to do.`;
}

/** The line for `state.pickedUp`. Pure; exported for tests. */
export function pickedUpText(pickedUp: GroomingQueuePickedUp, left: number | null, clock?: LocalClockOptions): string {
  switch (pickedUp.after) {
    case "wake":
      return queuePickedUpBanner({ wokeAt: pickedUp.at, ...(left === null ? {} : { left }), ...(clock ? { clock } : {}) });
    case "fix": {
      const when = formatLocalClock(pickedUp.at, clock);
      const head = when ? `Picked up again after the fix ${when}.` : "Picked up again after the fix.";
      return left === null ? head : `${head} ${briefsLeft(left)}`;
    }
    case "connection": {
      const when = formatLocalClock(pickedUp.at, clock);
      return when ? `Picked up again when the connection came back ${when}.` : "Picked up again when the connection came back.";
    }
  }
}

export interface QueuePickedUpLineProps {
  pickedUp: GroomingQueuePickedUp;
  /** Entries still to do (running and waiting). */
  left: number;
  /** For tests: "now" and the time zone. */
  clock?: LocalClockOptions;
}

export function QueuePickedUpLine({ pickedUp, left, clock }: QueuePickedUpLineProps) {
  return (
    <div
      role="status"
      className="flex items-center gap-3 rounded-[10px] border border-[#1F1F23] bg-[#111113] px-4 py-2.5"
      data-testid="queue-picked-up"
      data-after={pickedUp.after}
    >
      <RotateCcw className="h-4 w-4 flex-none text-[#6CC4FF]" aria-hidden="true" />
      <p className={cn("min-w-0 flex-1 text-foreground", TOOLBOX_TEXT.content)}>{pickedUpText(pickedUp, left, clock)}</p>
    </div>
  );
}
